import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Inbox, Reply, Archive, RefreshCw, ExternalLink, History as HistoryIcon, ChevronDown, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import AIDraftDialog from "@/components/AIDraftDialog";
import DashboardGridWrapper from "@/components/dashboard/DashboardGridWrapper";
import PageHeader from "@/components/dashboard/PageHeader";

const STATUS_TABS = [
    { key: "received", label: "Unread",   Icon: Inbox },
    { key: "replied",  label: "Replied",  Icon: Reply },
    { key: "archived", label: "Archived", Icon: Archive },
];

function timeAgo(iso) {
    if (!iso) return "";
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1)  return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
}

function statusBadgeClass(status) {
    switch (status) {
        case "received": return "bg-primary/10 text-primary border-primary/30";
        case "replied":  return "bg-success/10 text-success border-success/30";
        case "archived": return "bg-muted text-muted-foreground border-border";
        default:         return "bg-muted text-muted-foreground border-border";
    }
}

const PAGE_SIZE = 25;

export default function AdminInbox() {
    const [searchParams, setSearchParams] = useSearchParams();
    const openParam = searchParams.get("open");
    const [tab, setTab]                 = useState("received");
    const [submissions, setSubmissions] = useState([]);
    const [counts, setCounts]           = useState({ received: 0, replied: 0, archived: 0, total: 0 });
    const [loading, setLoading]         = useState(false);
    const [expanded, setExpanded]       = useState(null);
    const [pending, setPending]         = useState(new Set());   // ids with in-flight PATCH
    const [nextCursor, setNextCursor]   = useState(null);
    const [hasMore, setHasMore]         = useState(false);
    const [historyFor, setHistoryFor]   = useState(null);        // { id, entries }
    const [aiDraftFor, setAiDraftFor]   = useState(null);        // full submission for AI draft

    const setRowPending = (id, on) => {
        setPending((prev) => {
            const next = new Set(prev);
            on ? next.add(id) : next.delete(id);
            return next;
        });
    };

    const load = async (which = tab, { append = false, cursor = null } = {}) => {
        setLoading(true);
        try {
            const qs = new URLSearchParams({ status: which, limit: String(PAGE_SIZE) });
            if (cursor) qs.set("cursor", cursor);
            const { data } = await api.get(`/admin/inbox?${qs.toString()}`);
            setSubmissions((prev) => append ? [...prev, ...(data.submissions || [])] : (data.submissions || []));
            setCounts(data.counts || {});
            setNextCursor(data.next_cursor || null);
            setHasMore(!!data.has_more);
        } catch {
            toast.error("Failed to load inbox");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setExpanded(null);
        setNextCursor(null);
        setHasMore(false);
        load(tab, { append: false, cursor: null });
    }, [tab]);

    // Deep-link support: /admin/inbox?open={id} auto-expands & scrolls to that row.
    // If the target row is not on the current page for the active tab, keep loading
    // until it lands (bounded loops so we don't spin forever if the row was deleted).
    useEffect(() => {
        if (!openParam) return;
        if (submissions.some((s) => s.id === openParam)) {
            setExpanded(openParam);
            requestAnimationFrame(() => {
                const el = document.querySelector(`[data-testid="admin-inbox-row-${openParam}"]`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            });
            // Clear the query param so refresh/back doesn't re-trigger the effect
            searchParams.delete("open");
            setSearchParams(searchParams, { replace: true });
        }
    }, [openParam, submissions]);

    const updateStatus = async (id, next) => {
        setRowPending(id, true);
        try {
            await api.patch(`/admin/inbox/${id}/status`, { status: next });
            toast.success(`Marked as ${next}`);
            // Reload current tab from scratch (row usually moves to another tab)
            load(tab, { append: false, cursor: null });
        } catch {
            toast.error("Update failed");
        } finally {
            setRowPending(id, false);
        }
    };

    const openHistory = async (id) => {
        setHistoryFor({ id, loading: true, entries: [], current_status: null, created_ts: null });
        try {
            const { data } = await api.get(`/admin/inbox/${id}/history`);
            setHistoryFor({ id, loading: false, entries: data.history || [], current_status: data.current_status, created_ts: data.created_ts });
        } catch {
            toast.error("Failed to load history");
            setHistoryFor(null);
        }
    };

    const inboxWidgets = [
        {
            id: "inbox-list", title: "Submissions", category: "Inbox", layout: { x: 0, y: 0, w: 12, h: 14 }, minW: 6, minH: 6,
            render: () => (
                loading && submissions.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>
                ) : submissions.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-12 text-center border border-dashed border-border rounded-lg"
                         data-testid="admin-inbox-empty">
                        No {tab} submissions yet.
                    </div>
                ) : (
                    <>
                        <div className="space-y-3" data-testid="admin-inbox-list">
                            {submissions.map((s) => {
                                const isOpen  = expanded === s.id;
                                const isBusy  = pending.has(s.id);
                                const firstName = (s.name || "").split(" ")[0] || "there";
                                const mailto  = `mailto:${s.email}?subject=${encodeURIComponent("Re: your Melian Dialogue query")}&body=${encodeURIComponent(`Hello ${firstName},\n\nThanks for reaching out. Regarding your note:\n\n> ${s.message}\n\n`)}`;
                                return (
                                    <div key={s.id}
                                         className={`rounded-xl border border-border bg-card overflow-hidden ${isBusy ? "opacity-70" : ""}`}
                                         data-testid={`admin-inbox-row-${s.id}`}>
                                        <button
                                            className="w-full grid grid-cols-[1fr_auto] md:grid-cols-[220px_1fr_auto] gap-3 items-center px-4 py-3 text-left hover:bg-muted/40 transition-colors"
                                            onClick={() => setExpanded(isOpen ? null : s.id)}
                                            data-testid={`admin-inbox-row-toggle-${s.id}`}>
                                            <div>
                                                <div className="font-bold text-sm truncate">{s.name}</div>
                                                <div className="text-xs text-muted-foreground truncate">{s.email}</div>
                                            </div>
                                            <div className="hidden md:block text-sm text-foreground/80 truncate">
                                                {s.message}
                                            </div>
                                            <div className="flex items-center gap-2 shrink-0">
                                                <span className={`text-[10px] uppercase tracking-widest font-bold border px-2 py-0.5 rounded ${statusBadgeClass(s.processed_status)}`}>
                                                    {s.processed_status}
                                                </span>
                                                <span className="text-xs text-muted-foreground whitespace-nowrap">{timeAgo(s.ts)}</span>
                                            </div>
                                        </button>

                                        {isOpen && (
                                            <div className="px-4 pb-4 pt-1 space-y-3 border-t border-border/50 bg-muted/20"
                                                 data-testid={`admin-inbox-row-detail-${s.id}`}>
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                                                    <Detail label="Email"  value={s.email} />
                                                    <Detail label="Phone"  value={s.phone} />
                                                    <Detail label="IP"     value={s.ip || "—"} />
                                                </div>
                                                <div>
                                                    <div className="overline text-primary mb-1">Message</div>
                                                    <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">
                                                        {s.message}
                                                    </p>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-2 pt-2">
                                                    <a href={mailto}
                                                       className={`inline-flex items-center gap-2 bg-primary text-primary-foreground text-sm font-extrabold px-4 py-2 rounded-lg hover:opacity-90 transition-opacity ${isBusy ? "pointer-events-none opacity-50" : ""}`}
                                                       data-testid={`admin-inbox-reply-${s.id}`}>
                                                        <Reply size={14} />
                                                        Reply via email
                                                        <ExternalLink size={12} className="opacity-60" />
                                                    </a>
                                                    {s.processed_status !== "replied" && (
                                                        <Button variant="outline" size="sm"
                                                                disabled={isBusy}
                                                                onClick={() => updateStatus(s.id, "replied")}
                                                                data-testid={`admin-inbox-mark-replied-${s.id}`}>
                                                            {isBusy ? "Updating…" : "Mark replied"}
                                                        </Button>
                                                    )}
                                                    {s.processed_status !== "archived" && (
                                                        <Button variant="outline" size="sm"
                                                                disabled={isBusy}
                                                                onClick={() => updateStatus(s.id, "archived")}
                                                                data-testid={`admin-inbox-archive-${s.id}`}>
                                                            {isBusy ? "Updating…" : "Archive"}
                                                        </Button>
                                                    )}
                                                    {s.processed_status !== "received" && (
                                                        <Button variant="ghost" size="sm"
                                                                disabled={isBusy}
                                                                onClick={() => updateStatus(s.id, "received")}
                                                                data-testid={`admin-inbox-reopen-${s.id}`}>
                                                            {isBusy ? "Updating…" : "Reopen"}
                                                        </Button>
                                                    )}
                                                    <Button variant="ghost" size="sm"
                                                            disabled={isBusy}
                                                            onClick={() => setAiDraftFor(s)}
                                                            data-testid={`admin-inbox-ai-draft-${s.id}`}>
                                                        <Sparkles size={14} className="mr-1" />
                                                        AI draft
                                                    </Button>
                                                    <Button variant="ghost" size="sm"
                                                            disabled={isBusy}
                                                            onClick={() => openHistory(s.id)}
                                                            className="ml-auto"
                                                            data-testid={`admin-inbox-history-${s.id}`}>
                                                        <HistoryIcon size={14} className="mr-1" />
                                                        History
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {hasMore && (
                            <div className="pt-2 flex justify-center">
                                <Button variant="outline" size="sm"
                                        disabled={loading}
                                        onClick={() => load(tab, { append: true, cursor: nextCursor })}
                                        data-testid="admin-inbox-load-more">
                                    <ChevronDown size={14} className="mr-1" />
                                    {loading ? "Loading…" : "Load older"}
                                </Button>
                            </div>
                        )}
                    </>
                )
            ),
        },
    ];

    return (
        <div className="space-y-6" data-testid="admin-inbox-page">
            <PageHeader
                icon={Inbox}
                eyebrow="Admin Tools"
                title="Inbox"
                subtitle={`${counts.total} total · ${counts.received} unread`}
                actions={
                    <Button variant="outline" size="sm" onClick={() => load(tab, { append: false, cursor: null })}
                            data-testid="admin-inbox-refresh">
                        <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>
                }
                testId="admin-inbox-header"
            />

            {/* Tabs */}
            <div className="flex gap-2 border-b border-border" data-testid="admin-inbox-tabs">
                {STATUS_TABS.map(({ key, label, Icon }) => {
                    const active = tab === key;
                    return (
                        <button key={key} onClick={() => setTab(key)}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                                active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                            }`}
                            data-testid={`admin-inbox-tab-${key}`}>
                            <Icon size={14} />
                            {label}
                            <span className={`text-xs px-1.5 py-0.5 rounded ${active ? "bg-primary/15" : "bg-muted"}`}>
                                {counts[key] ?? 0}
                            </span>
                        </button>
                    );
                })}
            </div>

            <DashboardGridWrapper role="SUPER_ADMIN" viewId="admin-inbox" widgets={inboxWidgets} />

            {/* History drawer */}
            {historyFor && (
                <HistoryDrawer info={historyFor} onClose={() => setHistoryFor(null)} />
            )}

            {/* AI draft dialog */}
            {aiDraftFor && (
                <AIDraftDialog submission={aiDraftFor} onClose={() => setAiDraftFor(null)} />
            )}
        </div>
    );
}

function Detail({ label, value }) {
    return (
        <div>
            <div className="overline mb-0.5">{label}</div>
            <div className="text-sm text-foreground/90 break-all">{value}</div>
        </div>
    );
}

function HistoryDrawer({ info, onClose }) {
    return (
        <>
            <div className="fixed inset-0 bg-black/50 z-[80]" onClick={onClose} data-testid="admin-inbox-history-overlay" />
            <aside
                className="fixed top-0 right-0 z-[81] h-screen w-full sm:w-[420px] bg-card border-l border-border shadow-2xl overflow-y-auto"
                role="dialog"
                aria-label="Submission status history"
                data-testid="admin-inbox-history-drawer"
            >
                <div className="p-5 border-b border-border flex items-center justify-between">
                    <div>
                        <div className="overline text-primary mb-1">Audit trail</div>
                        <div className="font-extrabold text-lg tracking-tight">Status history</div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={onClose} data-testid="admin-inbox-history-close">Close</Button>
                </div>
                <div className="p-5 space-y-3">
                    <div className="text-xs text-muted-foreground">
                        Current: <span className="font-bold text-foreground uppercase tracking-widest">{info.current_status}</span>
                        {info.created_ts && (
                            <span> · created {timeAgo(info.created_ts)}</span>
                        )}
                    </div>

                    {info.loading ? (
                        <div className="text-sm text-muted-foreground">Loading…</div>
                    ) : (info.entries || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground" data-testid="admin-inbox-history-empty">
                            No status transitions yet. The submission is still in its original state.
                        </div>
                    ) : (
                        <ol className="relative border-l border-border pl-4 space-y-4" data-testid="admin-inbox-history-list">
                            {info.entries.slice().reverse().map((h, i) => (
                                <li key={h.ts || `hist-${i}`} className="relative">
                                    <span className="absolute -left-[21px] top-1 w-3 h-3 rounded-full bg-primary" aria-hidden />
                                    <div className="text-xs text-muted-foreground">{timeAgo(h.ts)}</div>
                                    <div className="text-sm">
                                        <span className="font-semibold uppercase tracking-widest text-muted-foreground">{h.from}</span>
                                        {" → "}
                                        <span className="font-semibold uppercase tracking-widest text-primary">{h.to}</span>
                                    </div>
                                    <div className="text-xs text-muted-foreground">by {h.by || "system"}</div>
                                    {h.note && <div className="text-xs text-foreground/80 mt-1 italic">"{h.note}"</div>}
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            </aside>
        </>
    );
}
